import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Shield, Copy, Check, AlertTriangle, Download, X } from 'lucide-react';
import api from '../services/apiClient';

export default function TwoFactorSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ enabled: false, backupCodesRemaining: 0 });
  const [setupStep, setSetupStep] = useState(null); // null, 'qr', 'verify', 'backup'
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/2fa/status');
      if (response && response.enabled !== undefined) {
        setStatus(response);
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const handleSetup = async () => {
    if (loading) return; // Prevent multiple clicks
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/2fa/setup');
      
      // apiClient already unwraps response.data.data, so response IS the data
      if (response && response.qrCode && response.manualEntryKey) {
        setQrCode(response.qrCode);
        setManualKey(response.manualEntryKey);
        setSetupStep('qr');
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (err) {
      console.error('2FA setup failed:', err.response?.data?.message || err.message);
      setError(err.response?.data?.message || err.message || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/2fa/verify', {
        token: verificationCode
      });
      
      if (response && response.backupCodes) {
        setBackupCodes(response.backupCodes);
        setSetupStep('backup');
        setSuccess('Two-factor authentication enabled successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await api.post('/2fa/disable', { password });
      
      setSuccess('Two-factor authentication disabled');
      setStatus({ enabled: false, backupCodesRemaining: 0 });
      setPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    
    if (!confirm('This will invalidate all existing backup codes. Continue?')) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/2fa/regenerate-backup-codes', { password });
      
      if (response && response.backupCodes) {
        setBackupCodes(response.backupCodes);
        setSetupStep('backup');
        setSuccess('New backup codes generated');
        setPassword('');
        await fetchStatus();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'key') {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      } else {
        setCopiedCode(text);
        setTimeout(() => setCopiedCode(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadBackupCodes = () => {
    const text = backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'xlicense-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const completeSetup = () => {
    setSetupStep(null);
    setVerificationCode('');
    setBackupCodes([]);
    fetchStatus();
  };

  // Render modal content
  const renderModal = () => {
    if (!setupStep) return null;

    const modalContent = setupStep === 'qr' ? (
      // QR Code Step
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Setup Two-Factor Authentication
            </h2>
            <button
              onClick={() => {
                setSetupStep(null);
                setQrCode('');
                setManualKey('');
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-6">
            <div>
              <p className="text-gray-700 mb-4">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
              </p>
              
              <div className="flex justify-center mb-4">
                <img src={qrCode} alt="QR Code" className="border-4 border-gray-200 rounded-lg" />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Can't scan? Enter this key manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-sm break-all">
                    {manualKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(manualKey, 'key')}
                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedKey ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setSetupStep('verify')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              I've Scanned the QR Code
            </button>
            
            <button
              onClick={() => {
                setSetupStep(null);
                setQrCode('');
                setManualKey('');
              }}
              className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    ) : setupStep === 'verify' ? (
      // Verify Step
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Verify Setup</h2>
            <button
              onClick={() => setSetupStep(null)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter the 6-digit code from your authenticator app
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                placeholder="123456"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                required
              />
            </div>
            
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertTriangle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify and Enable'}
            </button>
            
            <button
              type="button"
              onClick={() => setSetupStep('qr')}
              className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Back to QR Code
            </button>
          </form>
        </div>
      </div>
    ) : setupStep === 'backup' ? (
      // Backup Codes Step
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-green-600">Success!</h2>
            <button
              onClick={completeSetup}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-6">
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                {success}
              </div>
            )}
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                <div>
                  <p className="font-medium text-yellow-800 mb-1">Save Your Backup Codes</p>
                  <p className="text-sm text-yellow-700">
                    Store these codes in a safe place. Each code can be used once if you lose access to your authenticator app.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                {backupCodes.map((code, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                    <code className="flex-1 font-mono text-sm">{code}</code>
                    <button
                      onClick={() => copyToClipboard(code, index)}
                      className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                      title="Copy code"
                    >
                      {copiedCode === index ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={downloadBackupCodes}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Download Backup Codes
            </button>
            
            <button
              onClick={completeSetup}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
            >
              I've Saved My Backup Codes
            </button>
          </div>
        </div>
      </div>
    ) : null;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        {modalContent}
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderModal()}
      <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Shield className="w-6 h-6 text-blue-600" />
          Two-Factor Authentication
        </h2>
        
        {error && (
          <div className="mb-4 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}
        
        <div className="space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Status</p>
              <p className="text-sm text-gray-600 mt-1">
                {status.enabled ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled'}
              </p>
              {status.enabled && status.enabledAt && (
                <p className="text-xs text-gray-500 mt-1">
                  Enabled on {new Date(status.enabledAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              status.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {status.enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
          
          {/* Description */}
          <div className="text-gray-700">
            <p className="mb-2">
              Two-factor authentication (2FA) adds an extra layer of security to your account by requiring both your password and a verification code from your phone.
            </p>
            <p className="text-sm text-gray-600">
              Recommended for all users to protect against unauthorized access.
            </p>
          </div>
          
          {!status.enabled ? (
            // Enable 2FA
            <button
              onClick={handleSetup}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
            </button>
          ) : (
            <>
              {/* Backup Codes Info */}
              {status.backupCodesRemaining !== undefined && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">Backup Codes:</span> {status.backupCodesRemaining} remaining
                  </p>
                  {status.backupCodesRemaining < 3 && (
                    <p className="text-sm text-blue-700 mt-1">
                      You're running low on backup codes. Consider regenerating them.
                    </p>
                  )}
                </div>
              )}
              
              {/* Regenerate Backup Codes */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Regenerate Backup Codes</h3>
                <form onSubmit={handleRegenerateBackupCodes} className="space-y-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate New Backup Codes'}
                  </button>
                </form>
              </div>
              
              {/* Disable 2FA */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Disable Two-Factor Authentication</h3>
                <form onSubmit={handleDisable} className="space-y-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
